import React, { useEffect, useMemo, useRef } from 'react';
import { Select, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';

import { RootState } from 'app/store';
import SupportTicketApi from 'apis/api/SupportTicket';
import { getStructureTags, updateSupportTicketBranch, updateSupportTicketState } from '@slice/select.slice';

export const SUPPORT_BRANCH_LABELS: Record<string, string> = {
  AD: 'Ánh Dương',
  BM: 'Bình Minh'
};

// Chi nhánh/nhóm phụ trách của CSKH — trước đây nằm trong filter bar riêng của trang Quản lý phiếu,
// giờ đưa lên header cạnh ô tìm kiếm nhanh (DropdownResultSearch) để dễ thao tác + tái dùng được cho
// các trang CSKH khác sau này (lưu vào store chung select.slice, không phải state riêng của 1 trang).
// Hiện chỉ hiện khi đang ở trong menu Hỗ trợ CSKH — activeMenuHeader do Sider.tsx cập nhật khi bấm menu.
// labeled = true: kiểu thanh lọc trang (nhãn "Công ty" / "Nhóm" phía trên, giống tab Lịch sử của Quản lý phiếu).
// Mặc định: kiểu gọn trên header app.
// saveAssign = true: đổi lựa chọn thì tự lưu thành cấu hình phụ trách (StaffAssignTag) - chỉ nên bật cho CSKH;
// vai trò khác (Admin, Root...) chỉ dùng để lọc, không ghi đè cấu hình phụ trách của họ.
export default function CskhQuickFilter({
  labeled = false,
  saveAssign = true
}: {
  labeled?: boolean;
  saveAssign?: boolean;
}) {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const activeMenuHeader = useSelector((state: RootState) => state.select.activeMenuHeader);

  const branchFilter = useSelector((state: RootState) => state.select.supportTicketBranch);
  const stateFilter = useSelector((state: RootState) => state.select.supportTicketState);
  const structureTags = useSelector((state: RootState) => state.select.structureTags);
  const structureTagsLoaded = useSelector((state: RootState) => state.select.loadedData.structureTagsLoaded);

  // const isCskhRoute =
  //   activeMenuHeader?.keysMenu?.startsWith?.('cskh-management') ||
  //   (Array.isArray(activeMenuHeader?.openTabs) && activeMenuHeader.openTabs.includes('cskh-management'));

  useEffect(() => {
    // if (!isCskhRoute) return;
    if (!structureTagsLoaded) dispatch(getStructureTags());
  }, [structureTagsLoaded, dispatch]);
  // isCskhRoute,

  // Thêm "Tất cả" thành 1 option chọn thẳng trong danh sách — trước đây chỉ có qua nút xóa (X) của
  // allowClear, nên muốn đổi từ 1 lựa chọn cụ thể về "Tất cả" phải bấm xóa trước rồi mới mở lại được.
  //
  // QUAN TRỌNG: danh sách option KHÔNG được lấy từ assignedTag — vì mỗi lần đổi filter, effect bên dưới
  // tự lưu StaffAssignTag đúng bằng giá trị vừa chọn (vd chỉ còn "BM"), nên assignedTag.branchs/tags sẽ
  // bị THU HẸP lại theo, kéo theo option cũng biến mất dần (chọn BM thì AD "mất tích" khỏi danh sách).
  // Option phải lấy từ danh sách CỐ ĐỊNH/TOÀN BỘ (SUPPORT_BRANCH_LABELS, structureTags) — độc lập với
  // cấu hình phụ trách hiện tại đang bị chính việc chọn filter ghi đè.
  const branchOptions = useMemo(
    () => [
      { value: '-1', label: 'Tất cả' },
      ...Object.keys(SUPPORT_BRANCH_LABELS).map(code => ({ value: code, label: SUPPORT_BRANCH_LABELS[code] }))
    ],
    []
  );

  const tagOptions = useMemo(
    () => [{ value: '-1', label: 'Tất cả' }, ...(structureTags || []).map(tag => ({ value: tag.id, label: tag.name }))],
    [structureTags]
  );

  // Đổi filter -> lưu luôn thành cấu hình phụ trách (StaffAssignTag). Bỏ qua lần chạy đầu (mount)
  // để không tự ghi đè cấu hình cũ khi chưa ai đổi gì.
  const isFirstAssignRun = useRef(true);
  useEffect(() => {
    // if (!isCskhRoute) return;
    if (isFirstAssignRun.current) {
      isFirstAssignRun.current = false;
      return;
    }
    if (!saveAssign) return; // không phải CSKH: chỉ lọc, không lưu cấu hình phụ trách
    if (!stateFilter && !structureTagsLoaded) return;
    const branchs = branchFilter ? [branchFilter] : Object.keys(SUPPORT_BRANCH_LABELS);
    const isValidFilter = stateFilter !== null && stateFilter !== undefined;
    const parsedFilter = Number(stateFilter);
    const tagIds =
      isValidFilter && !isNaN(parsedFilter)
        ? [parsedFilter]
        : (structureTags || []).map(tag => tag?.id).filter((id): id is number => id != null);
    const payload = { branchs, tagIds };
    SupportTicketApi.StaffAssignTag(payload)
      .then(res => {
        if (res?.data?.error) {
          console.warn('StaffAssignTag lỗi:', payload, res?.data);
          message.error(res?.data?.message || 'Lưu cấu hình phụ trách thất bại!');
        }
      })
      .catch(error => {
        console.warn('StaffAssignTag exception:', payload, error?.response?.data || error);
        message.error('Xảy ra lỗi khi lưu cấu hình phụ trách!');
      });
  }, [branchFilter, stateFilter, structureTagsLoaded, saveAssign]); // isCskhRoute,

  // if (!isCskhRoute) return null;

  if (labeled) {
    return (
      <>
        <div className="flex flex-col">
          <span className="text-xs font-semibold">Công ty</span>
          <Select
            style={{ width: 115 }}
            value={branchFilter || '-1'}
            onChange={v => dispatch(updateSupportTicketBranch(v || '-1'))}
            options={branchOptions}
          />
        </div>
        <div className="flex flex-col">
          <span className="text-xs font-semibold">Nhóm</span>
          <Select
            style={{ width: 140 }}
            value={stateFilter || '-1'}
            onChange={v => dispatch(updateSupportTicketState(v || '-1'))}
            showSearch
            optionFilterProp="label"
            options={tagOptions}
          />
        </div>
      </>
    );
  }

  return (
    <div className="flex items-center gap-1.5 mr-2">
      <Select
        size="small"
        style={{ width: 140 }}
        value={branchFilter || '-1'}
        onChange={v => dispatch(updateSupportTicketBranch(v || '-1'))}
        options={branchOptions}
      />
      <Select
        size="small"
        style={{ width: 160 }}
        value={stateFilter || '-1'}
        onChange={v => dispatch(updateSupportTicketState(v || '-1'))}
        options={tagOptions}
      />
    </div>
  );
}
